// Phase 100 — reusable flat-list editor for Master Taxonomy Settings
// (Designations, Vendor Types). Add / rename / remove values, Save writes
// the full list via PUT /taxonomy/lists/{name}.
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function SimpleTaxonomyListEditor({ listName, title, subtitle }) {
  const [values, setValues] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/taxonomy/lists/${listName}`)
      .then(({ data }) => setValues(data?.values || []))
      .catch((e) => { toast.error(formatApiError(e)); setValues([]); });
  };
  useEffect(() => { load(); }, [listName]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) { toast.error("Already in the list"); return; }
    setValues([...values, v]);
    setDraft("");
  };
  const remove = (idx) => setValues(values.filter((_, i) => i !== idx));
  const rename = (idx, v) => setValues(values.map((x, i) => (i === idx ? v : x)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/taxonomy/lists/${listName}`, { values });
      setValues(data.values);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (values === null) {
    return <div className="p-8 text-center text-slate-500 flex items-center justify-center"><Loader2 size={16} className="animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid={`taxonomy-list-${listName}`}>
      <div>
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      <div className="flex items-center gap-2">
        <Input
          className="max-w-xs"
          placeholder={`+ Add to ${title}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          data-testid={`taxonomy-list-${listName}-input`}
        />
        <Button variant="outline" size="sm" onClick={add} disabled={!draft.trim()} data-testid={`taxonomy-list-${listName}-add-btn`}>
          <Plus size={14} className="mr-1.5" /> Add
        </Button>
      </div>
      {values.length === 0 ? (
        <div className="p-8 text-center text-slate-400 border border-dashed border-slate-300 rounded-sm text-sm">No values yet — add one above.</div>
      ) : (
        <div className="space-y-1.5" data-testid={`taxonomy-list-${listName}-rows`}>
          {values.map((v, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input value={v} onChange={(e) => rename(idx, e.target.value)} className="h-8 max-w-xs text-sm" />
              <button onClick={() => remove(idx)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`taxonomy-list-${listName}-remove-${idx}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <Button
        size="sm" onClick={save} disabled={saving}
        className="bg-[#F54800] hover:bg-[#D63E00] text-white"
        data-testid={`taxonomy-list-${listName}-save-btn`}
      >
        {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />}
        Save
      </Button>
    </div>
  );
}
